// =============================================================================
// MeuDriver — Analytics (Posthog)
// -----------------------------------------------------------------------------
// Decisão de produto: Posthog.
//   * SDK React maduro, free tier generoso (1M events/mês), self-host opcional
//     (PostHog Cloud EU é LGPD-friendly por default).
//   * Funil de eventos via UI sem precisar de SQL.
//   * Pode capturar session replays opt-in no futuro.
//
// Padrão de uso no código:
//   import { track } from '@/lib/analytics'
//   track('signup_completed', { role: 'student' })
//
// Convenções:
//   * Event names em snake_case, no passado: `signup_completed`, `booking_created`.
//   * Props sempre objeto plano (string/number/bool/null) — Posthog não gosta
//     de objetos aninhados.
//   * NÃO mandar PII (email, telefone, nome). Use `identify` para isso, que vai
//     para a área de pessoas (não como propriedade de evento).
// =============================================================================

import posthog from 'posthog-js'

const POSTHOG_KEY  = import.meta.env.VITE_POSTHOG_KEY as string | undefined
const POSTHOG_HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? 'https://us.i.posthog.com'

let initialized = false

/**
 * Inicializa o Posthog uma única vez. Idempotente — múltiplas chamadas no-op.
 *
 * Chamado uma vez em `main.tsx`. Em DEV (import.meta.env.DEV), pula init para
 * não poluir o projeto Posthog com hot-reload e desenvolvimento local.
 */
export function initAnalytics(): void {
  if (initialized) return
  if (!POSTHOG_KEY || POSTHOG_KEY.length === 0) return
  if (import.meta.env.DEV) return

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    autocapture: false,           // controlamos eventos manualmente
    capture_pageview: true,       // viewport / pageview automatico
    capture_pageleave: true,
    persistence: 'localStorage+cookie',
    person_profiles: 'identified_only',
  })

  initialized = true
}

/**
 * Captura um evento. No-op em DEV ou se a SDK não foi inicializada
 * (sem VITE_POSTHOG_KEY). Sempre seguro de chamar.
 */
export function track(event: string, props?: Record<string, unknown>): void {
  if (import.meta.env.DEV) return
  if (!initialized) return
  posthog.capture(event, props)
}

/**
 * Vincula a sessão atual a um user_id. Use APENAS depois do login (não no
 * signup — pode mandar email/telefone como propriedade por acidente).
 */
export function identify(userId: string, traits?: Record<string, unknown>): void {
  if (import.meta.env.DEV) return
  if (!initialized) return
  posthog.identify(userId, traits)
}

/**
 * Limpa a identificação local. Chamar no logout.
 */
export function resetAnalytics(): void {
  if (import.meta.env.DEV) return
  if (!initialized) return
  posthog.reset()
}
